import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';

export interface TocSection {
  id: string;
  title: string;
}

interface LegalLayoutProps {
  title: string;
  subtitle: string;
  lastUpdated: string;
  activeTab: 'privacy' | 'terms' | 'cookies';
  sections: TocSection[];
  children: React.ReactNode;
}

const cleanTocTitle = (title: string) => {
  return title.replace(/^(\d+|[\u0660-\u0669]+)[.)]\s*/, '');
};

const LegalLayout: React.FC<LegalLayoutProps> = ({
  title,
  subtitle,
  lastUpdated,
  activeTab,
  sections,
  children,
}) => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.id || '');
  const [mobileTocOpen, setMobileTocOpen] = useState<boolean>(false);

  // Reset active section when route or sections change
  useEffect(() => {
    if (sections.length > 0) {
      setActiveSection(sections[0].id);
    }
  }, [location.pathname, activeTab, sections]);

  // Handle active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      // If at top of page, default to first section
      if (window.scrollY < 300 && sections.length > 0) {
        setActiveSection(sections[0].id);
        return;
      }

      const scrollPosition = window.scrollY + 250;
      let currentSectionId = sections[0]?.id || '';
      for (let i = 0; i < sections.length; i++) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          currentSectionId = sections[i].id;
        }
      }
      if (currentSectionId) {
        setActiveSection(currentSectionId);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run after a tiny delay so window.scrollTo(0,0) from route change has applied
    const timer = setTimeout(handleScroll, 50);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [sections]);

  const tabs = [
    {
      id: 'privacy',
      label: t('footer.privacy', 'Gizlilik Politikası'),
      path: `/${language}/gizlilik-politikasi`,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      id: 'terms',
      label: t('footer.terms', 'Kullanım Şartları'),
      path: `/${language}/kullanim-sartlari`,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      id: 'cookies',
      label: t('footer.cookies', 'Çerez Politikası'),
      path: `/${language}/cerez-politikasi`,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  const scrollToSection = (id: string) => {
    setMobileTocOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen py-6 md:py-12 animate-in fade-in duration-700">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-brand-purple-900 text-white py-16 md:py-20 mb-12 rounded-[2.5rem] shadow-2xl">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-purple-500/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-yellow-400/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-xs font-bold uppercase tracking-widest text-brand-purple-200/70 mb-6">
            <Link to={`/${language}`} className="hover:text-brand-yellow-400 transition-colors">
              {t('nav.home', 'Ana Sayfa')}
            </Link>
            <span>/</span>
            <span className="text-brand-yellow-400">{t('legal.badge', 'Yasal Bilgilendirme')}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="inline-block px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md text-brand-yellow-400 text-xs font-semibold tracking-wider uppercase mb-4 border border-white/10">
                {subtitle}
              </span>
              <h1 className="text-3xl md:text-5xl font-serif font-bold text-white leading-tight">
                {title}
              </h1>
            </div>

            <div className="flex items-center space-x-2 text-xs md:text-sm text-brand-purple-100/80 bg-white/5 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/10 self-start md:self-auto">
              <svg className="w-4 h-4 text-brand-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{t('legal.last_updated', 'Son Güncelleme:')} <strong>{lastUpdated}</strong></span>
            </div>
          </div>

          {/* Legal Pages Navigation Tabs */}
          <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap gap-3">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  className={`flex items-center space-x-2.5 px-5 py-3 rounded-2xl font-bold text-xs md:text-sm transition-all duration-300 ${
                    isActive
                      ? 'bg-brand-yellow-400 text-brand-purple-900 shadow-lg shadow-brand-yellow-400/20 scale-105'
                      : 'bg-white/10 text-white hover:bg-white/20 hover:text-brand-yellow-400 backdrop-blur-md'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile Accordion Toggle for TOC */}
        <div className="lg:hidden mb-8">
          <button
            onClick={() => setMobileTocOpen(!mobileTocOpen)}
            className="w-full bg-white p-4 rounded-2xl border border-brand-purple-100 shadow-sm flex items-center justify-between font-bold text-brand-purple-900"
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-brand-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              <span>{t('legal.toc_title', 'İçindekiler')} ({sections.length} {language === 'ar' ? 'عناوين' : language === 'en' ? 'Sections' : language === 'krm' ? 'Beş' : 'Başlık'})</span>
            </div>
            <svg
              className={`w-5 h-5 transition-transform duration-300 ${mobileTocOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {mobileTocOpen && (
            <div className="mt-2 bg-white rounded-2xl p-4 border border-brand-purple-100 shadow-lg space-y-1 animate-in fade-in slide-in-from-top-2 duration-300">
              {sections.map((sec, idx) => (
                <button
                  key={sec.id}
                  onClick={() => scrollToSection(sec.id)}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                    activeSection === sec.id
                      ? 'bg-brand-purple-50 text-brand-purple-900 font-bold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-brand-purple-500 font-mono mr-2">{idx + 1}.</span>
                  {cleanTocTitle(sec.title)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Desktop Table of Contents Sidebar */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28">
            <div className="bg-white p-6 rounded-[2rem] border border-brand-purple-50 shadow-xl shadow-brand-purple-900/5 space-y-6">
              <div className="flex items-center space-x-3 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-xl bg-brand-purple-50 flex items-center justify-center text-brand-purple-700">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-serif font-bold text-brand-purple-900 text-lg">{t('legal.toc_title', 'İçindekiler')}</h3>
                  <p className="text-xs text-gray-400 font-light">{t('legal.toc_subtitle', 'Sayfa bölümlerine hızlı erişim')}</p>
                </div>
              </div>

              <nav className="space-y-1">
                {sections.map((sec, idx) => {
                  const isActive = activeSection === sec.id;
                  return (
                    <button
                      key={sec.id}
                      onClick={() => scrollToSection(sec.id)}
                      className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center group ${
                        isActive
                          ? 'bg-brand-purple-900 text-white font-bold shadow-md shadow-brand-purple-900/20 translate-x-1'
                          : 'text-gray-600 hover:bg-brand-purple-50 hover:text-brand-purple-900'
                      }`}
                    >
                      <span
                        className={`text-xs font-mono mr-3 w-5 h-5 rounded-full flex items-center justify-center ${
                          isActive
                            ? 'bg-brand-yellow-400 text-brand-purple-900 font-bold'
                            : 'bg-gray-100 text-gray-400 group-hover:bg-brand-purple-200 group-hover:text-brand-purple-900'
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="truncate">{cleanTocTitle(sec.title)}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Need help box */}
              <div className="pt-6 border-t border-gray-100">
                <div className="bg-brand-purple-50 p-5 rounded-2xl space-y-3">
                  <div className="flex items-center space-x-2 text-brand-purple-900 font-bold text-sm">
                    <svg className="w-5 h-5 text-brand-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>{t('legal.help_title', 'Sorunuz mu var?')}</span>
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed font-light">
                    {t('legal.help_desc', 'Yasal haklarınız veya KVKK başvurularınız için bizimle iletişime geçebilirsiniz.')}
                  </p>
                  <a
                    href="mailto:tja.bilgi@gmail.com"
                    className="inline-flex items-center space-x-2 text-xs font-bold text-brand-purple-700 hover:text-brand-purple-900 hover:underline"
                  >
                    <span>tja.bilgi@gmail.com</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Legal Document Content */}
          <main className="lg:col-span-8 bg-white p-8 sm:p-12 rounded-[2.5rem] border border-brand-purple-50 shadow-xl shadow-brand-purple-900/5 space-y-12">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default LegalLayout;
